import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock, Linkedin, Twitter, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import { navLinks, company } from "@/mock/mock";
import { useQuote } from "@/components/QuoteDialog";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* top bar */}
      <div className="hidden lg:block border-b border-white/5 bg-brand-navy/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-10 text-xs text-slate-400">
          <div className="flex items-center gap-6">
            <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-brand-lime transition-colors">
              <Mail className="h-3.5 w-3.5 text-brand-lime" /> {company.email}
            </a>
            <a href={`tel:${company.phoneHref}`} className="flex items-center gap-2 hover:text-brand-lime transition-colors">
              <Phone className="h-3.5 w-3.5 text-brand-lime" /> {company.phone}
            </a>
            <span className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-brand-lime" /> {company.hours}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href={company.social.linkedin} aria-label="LinkedIn" className="hover:text-brand-lime transition-colors"><Linkedin className="h-3.5 w-3.5" /></a>
            <a href={company.social.twitter} aria-label="Twitter" className="hover:text-brand-lime transition-colors"><Twitter className="h-3.5 w-3.5" /></a>
            <a href={company.social.facebook} aria-label="Facebook" className="hover:text-brand-lime transition-colors"><Facebook className="h-3.5 w-3.5" /></a>
          </div>
        </div>
      </div>

      {/* main nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-brand-navy/95 backdrop-blur-lg shadow-lg shadow-black/40 border-b border-white/5" : "bg-transparent"}`}>
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-[72px]">
          <Link to="/"><Logo /></Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-brand-lime" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Button onClick={openQuote} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full px-6">
              Get a Quote
            </Button>
          </div>

          {/* mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button aria-label="Menu" className="text-white p-2"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-navy2 border-white/10 text-white w-[300px]">
              <div className="flex items-center justify-between mb-8">
                <Logo />
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        isActive ? "bg-white/5 text-brand-lime" : "text-slate-300 hover:bg-white/5"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </nav>
              <Button onClick={() => { setOpen(false); openQuote(); }} className="mt-6 w-full bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full">
                Get a Quote
              </Button>
              <div className="mt-8 space-y-3 text-sm text-slate-400">
                <a href={`mailto:${company.email}`} className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-lime" /> {company.email}</a>
                <a href={`tel:${company.phoneHref}`} className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-lime" /> {company.phone}</a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
